import { cloneDeep } from 'lodash/fp';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';
import { useQueryParam } from 'use-query-params';

import WorkflowControlPanel from 'dashboard/components/WorkflowControlPanel';
import {
  findAndAddFirstBranchStep,
  findAndAddNextSteps,
  findAndReplaceSteps,
  omitStep,
  replaceWithNewTokens,
} from 'dashboard/components/WorkflowEditor/utils';
import WorkflowActionMetaProvider from 'dashboard/components/WorkflowEditor/WorkflowActionMetaProvider';
import { WorkflowEditorType } from 'dashboard/components/WorkflowEditor/WorkflowActionMetaProvider/WorkflowActionMetaProvider';
import WorkflowCanvas, {
  useCopyWorkflowSteps,
} from 'dashboard/components/WorkflowEditor/WorkflowCanvas';
import WorkflowVersionsSection from 'dashboard/components/WorkflowEditor/WorkflowVersionsSection';
import { BANNER_MIN_HEIGHT } from 'dashboard/components-lib/Banner/Banner';
import { FlowPlotterController } from 'dashboard/components-lib/FlowPlotter';
import { FULL_SCREEN_HEADER_HEIGHT } from 'dashboard/layouts/ShowViewLayoutV2';
import {
  generateStepContext,
  getActionStepFilters,
  updateReferences,
  UsableObjectConfig,
} from 'dashboard/lib/workflows';
import { TemplateFilter } from 'dashboard/lib/workflows/objects';
import { replaceReferencesInParams } from 'dashboard/lib/workflows/steps-config-operations';
import { TransactionType } from 'dashboard/models/transaction-type';
import { BaseTrigger, TRIGGER_EVENT_NAME_TO_OBJECT } from 'dashboard/models/trigger';
import { WorkflowError, WorkflowSchema } from 'dashboard/models/workflow';
import { WorkflowBlock } from 'dashboard/models/workflow-block';
import { WorkflowDeployment } from 'dashboard/models/workflow-deployment';
import {
  INPUT_STEP_TOKEN,
  newPlaceholderStep,
  outputStep,
  WorkflowStep,
  WorkflowStepAll,
  WorkflowStepInput,
  WorkflowStepType,
  WorkflowVersion,
  WorkflowVersionOutput,
} from 'dashboard/models/workflow-version';
import { setStepContexts } from 'dashboard/redux/actions/workflow';
import { useListExternalIntegrationVendorsQuery } from 'dashboard/redux/api/external-integration-vendor';
import {
  usePlatformObjectJsonSchema,
  useUsableObjects,
} from 'dashboard/redux/selectors/workflow-editor-context';
import { ObjectSchemaMap } from 'dashboard/redux/slices/object-schemas';
import useFeatureFlag from 'lib/hooks/useFeatureFlag';
import { PERMITTED_DOCUMENT_TAGS, useKeydownDeleteListener } from 'lib/hooks/useKeyListener';
import useOnMount from 'lib/hooks/useOnMount';
import { ApiModel } from 'lib/interfaces/api';
import { buildGraph } from 'lib/workflows';

export const WorkflowEditorContainer = styled.div<{
  isShowingHeaderStatus?: boolean;
  isShowingToolbar?: boolean;
}>`
  display: flex;
  ${({ isShowingHeaderStatus, isShowingToolbar }) => {
    const headerHeight =
      FULL_SCREEN_HEADER_HEIGHT +
      (isShowingHeaderStatus ? BANNER_MIN_HEIGHT : 0) +
      (isShowingToolbar ? 48 : 0);
    return `height: calc(100vh - ${headerHeight}px)`;
  }}
`;

export const isDeleteableStepType = (
  activeNode: WorkflowStepAll | null,
): activeNode is WorkflowStep => {
  switch (activeNode?.type) {
    case WorkflowStepType.Action:
    case WorkflowStepType.Conditional:
    case WorkflowStepType.Loop:
    case WorkflowStepType.Parallel:
    case WorkflowStepType.Placeholder:
    case WorkflowStepType.Wait:
      return true;
    default:
      return false;
  }
};

type VersionConfig = {
  description?: string;
  outputs: WorkflowVersionOutput[];
  'steps-config': WorkflowStep[];
  'time-out-minutes'?: number;
};

interface WorkflowEditorProps {
  editable: boolean;
  focusedValidationErrorIndex?: number;
  inputStep: WorkflowStepInput;
  isCurrentPublishedVersion?: boolean;
  isDirty?: boolean;
  isShowingHeaderStatus: boolean;
  isShowingWorkflowVersions: boolean;
  latestDraftVersionId: string;
  parentId: string;
  parentName: string;
  transactionTypes?: TransactionType[];
  validationErrors: Array<WorkflowError>;
  versionConfig: VersionConfig;
  versionId: string;
  workflowDeployment?: WorkflowDeployment;
  workflowSchema: WorkflowSchema;
  actionMetaLocation?: WorkflowEditorType;
  canAddModule?: boolean;
  isLoadingVersionList: boolean;
  versionsList: (WorkflowVersion | WorkflowBlock)[];
  clearFocusedValidationErrorIndex?: () => void;
  closeWorkflowVersionView: () => void;
  loadFilterFieldConfigs?: () => Promise<void>;
  loadWorkflowVersion: (workflowVersionId: string) => void;
  loadVersionList: () => Promise<void>;
  restoreDraftVersion: () => Promise<void>;
  handleDiscardDraft: () => Promise<void>;
  setInputStep: (inputStep: WorkflowStepInput) => void;
  updateVersionConfig: (versionConfig: Partial<VersionConfig>) => void;
  fetchPlatformObjectJsonSchema: (template?: TemplateFilter) => Promise<ObjectSchemaMap>;
}

const WorkflowEditor = ({
  editable,
  focusedValidationErrorIndex,
  inputStep,
  isCurrentPublishedVersion,
  isDirty,
  isShowingHeaderStatus,
  isShowingWorkflowVersions,
  latestDraftVersionId,
  parentId,
  parentName,
  transactionTypes,
  validationErrors,
  versionConfig,
  versionId,
  workflowDeployment,
  workflowSchema,
  actionMetaLocation = 'workflows',
  canAddModule = false,
  isLoadingVersionList,
  versionsList,
  clearFocusedValidationErrorIndex,
  closeWorkflowVersionView,
  loadFilterFieldConfigs,
  loadWorkflowVersion,
  loadVersionList,
  restoreDraftVersion,
  handleDiscardDraft,
  setInputStep,
  updateVersionConfig,
  fetchPlatformObjectJsonSchema,
}: WorkflowEditorProps) => {
  const {
    description,
    outputs,
    'steps-config': stepsConfig,
    'time-out-minutes': timeOutMinutes,
  } = versionConfig;
  const { trigger } = inputStep;

  const dispatch = useDispatch();
  const platformObjectJsonSchema = usePlatformObjectJsonSchema();
  const tokensToSteps = useMemo(() => buildGraph(stepsConfig), [stepsConfig]);
  const criteriaVariableSchemas = workflowSchema['criteria-variable-schemas'];
  const genericWorkflowActions = workflowSchema['generic-workflow-actions'];
  const { data: externalIntegrationVendors = [] } = useListExternalIntegrationVendorsQuery();
  const showErrorObjects = useFeatureFlag('global:workflow-error-variables');

  const [activeNodeToken, setActiveNodeToken] = useQueryParam<string | null>('step');
  const [focusedNodeToken, setFocusedNodeToken] = useState<string | null>(activeNodeToken ?? null);

  useOnMount(() => {
    if (activeNodeToken && !tokensToSteps[activeNodeToken]) {
      setActiveNodeToken(null);
    }
  });

  const activeNode = useMemo(() => {
    if (!activeNodeToken) {
      return null;
    }
    if (activeNodeToken === outputStep.token) {
      return outputStep;
    }

    const activeNodeStep = tokensToSteps[activeNodeToken]?.step;
    // don't fallback to trigger step for direct trigger workflows as it is not rendered in the tree
    const activeNodeFallback =
      inputStep.trigger?.type !== ApiModel.TriggerDirect ? inputStep : null;
    return activeNodeStep || activeNodeFallback;
  }, [activeNodeToken, tokensToSteps, inputStep]);
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  const [conditionalRouteToEdit, setConditionalRouteToEdit] = useState<number>(null);

  const [stepsCopied] = useCopyWorkflowSteps();

  const wrappedSetFocusedNodeToken = (path: string) => {
    const token = path.split('.')[0];
    setFocusedNodeToken(token === BaseTrigger ? INPUT_STEP_TOKEN : token);
  };

  const selectNode = useCallback(
    (selectedNodeToken: string | null) => {
      if (selectedNodeToken && isShowingWorkflowVersions) {
        // Both workflow versions and edit config panel occupies the same screen real estate
        // so automatically close the version history panel when we want to edit anything
        closeWorkflowVersionView();
      }
      setActiveNodeToken(selectedNodeToken);
      setFocusedNodeToken(selectedNodeToken);
    },
    [closeWorkflowVersionView, isShowingWorkflowVersions, setActiveNodeToken],
  );

  const updateInputStep = async (newInputStep: WorkflowStepInput, template?: TemplateFilter) => {
    const schema = await fetchPlatformObjectJsonSchema(template);
    const combinedSchema = platformObjectJsonSchema
      ? { ...platformObjectJsonSchema, ...schema }
      : undefined;
    dispatch(
      setStepContexts({
        [INPUT_STEP_TOKEN]: generateStepContext({
          step: newInputStep,
          criteriaVariableSchemas,
          platformObjectJsonSchema: combinedSchema,
          genericWorkflowActions,
          showErrorObjects,
        }),
      }),
    );
    setInputStep(newInputStep);
    if (
      trigger &&
      newInputStep.trigger &&
      'event-name' in trigger.attributes &&
      'event-name' in newInputStep.trigger.attributes &&
      TRIGGER_EVENT_NAME_TO_OBJECT[trigger.attributes['event-name']] !==
        TRIGGER_EVENT_NAME_TO_OBJECT[newInputStep.trigger.attributes['event-name']]
    ) {
      // updating the trigger object fundamentally changes the workflow, so reset steps-config & output
      updateVersionConfig({
        'steps-config': [],
        outputs: [],
      });
    }
  };

  const updateStepsConfig = useCallback(
    (newStepsConfig: WorkflowStep[]) => {
      updateVersionConfig({
        'steps-config': newStepsConfig,
      });
    },
    [updateVersionConfig],
  );

  const updateVersionOutputs = useCallback(
    (outputs: WorkflowVersionOutput[]) => {
      updateVersionConfig({
        outputs,
      });
    },
    [updateVersionConfig],
  );

  const updateStep = useCallback(
    async (newStep: WorkflowStep, skipContext?: boolean, template?: TemplateFilter) => {
      // Update step synchronously first to prevent input re-render jumps during async operations
      updateStepsConfig(
        findAndReplaceSteps(
          updateReferences({
            currentSteps: stepsConfig,
            targetReference: newStep,
            inputStep,
            criteriaVariableSchemas,
          }),
          newStep,
        ),
      );
      if (!skipContext) {
        const schema = await fetchPlatformObjectJsonSchema(template);
        const combinedSchema = platformObjectJsonSchema
          ? { ...platformObjectJsonSchema, ...schema }
          : undefined;
        dispatch(
          setStepContexts({
            [newStep.token]: generateStepContext({
              step: newStep,
              criteriaVariableSchemas,
              platformObjectJsonSchema: combinedSchema,
              genericWorkflowActions,
              showErrorObjects,
            }),
          }),
        );
      }
    },
    [
      updateStepsConfig,
      stepsConfig,
      inputStep,
      criteriaVariableSchemas,
      fetchPlatformObjectJsonSchema,
      dispatch,
      platformObjectJsonSchema,
      genericWorkflowActions,
      showErrorObjects,
    ],
  );

  const updateVersionExpiration = useCallback(
    (expiration: number) => {
      updateVersionConfig({
        'time-out-minutes': expiration,
      });
    },
    [updateVersionConfig],
  );

  const updateVersionDescription = useCallback(
    (description: string) => {
      updateVersionConfig({
        description,
      });
    },
    [updateVersionConfig],
  );

  const closePanel = () => {
    selectNode(null);
  };

  const deleteStep = (stepToDelete: WorkflowStep) => {
    closePanel();
    updateStepsConfig(omitStep(stepsConfig, stepToDelete.token));
  };

  const handleAddNode = (previousNodeToken: string) => {
    const placeholderStep = newPlaceholderStep();
    if (previousNodeToken === inputStep.token) {
      updateStepsConfig([placeholderStep, ...stepsConfig]);
    } else {
      updateStepsConfig(findAndAddNextSteps(stepsConfig, previousNodeToken, [placeholderStep]));
    }

    selectNode(placeholderStep.token);
  };

  const handleAddFirstChildNode = (parentNodeToken: string, branchIndex: number) => {
    const placeholderStep = newPlaceholderStep();
    updateStepsConfig(
      findAndAddFirstBranchStep(stepsConfig, placeholderStep, parentNodeToken, branchIndex),
    );

    selectNode(placeholderStep.token);
  };

  // Memoize with stepsConfig since this function is used for a dep of memoizing of react-dnd
  const moveStep = useCallback(
    (
      targetStepToken: string,
      previousOrParentOfDestinationNodeToken: string,
      destinationBranchIndex?: number,
    ) => {
      const movedStep = tokensToSteps[targetStepToken].step;
      const omitted = omitStep(stepsConfig, targetStepToken);

      if (typeof destinationBranchIndex === 'number') {
        // insert to top of branch
        updateStepsConfig(
          findAndAddFirstBranchStep(
            omitted,
            movedStep,
            previousOrParentOfDestinationNodeToken,
            destinationBranchIndex,
          ),
        );
      } else {
        // insert to immediate after a previous step
        if ([inputStep.token, BaseTrigger].includes(previousOrParentOfDestinationNodeToken)) {
          updateStepsConfig([movedStep, ...omitted]);
        } else {
          updateStepsConfig(
            findAndAddNextSteps(omitted, previousOrParentOfDestinationNodeToken, [movedStep]),
          );
        }
      }
    },
    [stepsConfig, updateStepsConfig, tokensToSteps, inputStep.token],
  );

  const pasteCopiedSteps = useCallback(
    async (holdStep: WorkflowStep) => {
      const { steps } = stepsCopied;
      if (steps && steps.length) {
        // NOTE - regenerate new tokens every time user pastes
        const { newSteps, tokenMap } = replaceWithNewTokens(cloneDeep(steps));
        tokenMap.set(steps[0].token, holdStep.token);
        const newStepsWithUpdatedReferences = replaceReferencesInParams(newSteps, tokenMap);
        const [firstStep, ...remainingSteps] = newStepsWithUpdatedReferences;
        firstStep.token = holdStep.token;
        let newStepsConfig = findAndAddNextSteps(
          findAndReplaceSteps(
            updateReferences({
              currentSteps: stepsConfig,
              targetReference: firstStep,
              inputStep,
              criteriaVariableSchemas,
            }),
            firstStep,
          ),
          firstStep.token,
          remainingSteps,
        );
        remainingSteps.forEach((step: WorkflowStep) => {
          newStepsConfig = updateReferences({
            currentSteps: newStepsConfig,
            targetReference: step,
            inputStep,
            criteriaVariableSchemas,
            checkUpdate: false,
          });
        });
        updateStepsConfig(newStepsConfig);

        const schemaRequests = newStepsWithUpdatedReferences.map((step) => {
          if (!('action' in step)) {
            return {};
          }
          const templates = getActionStepFilters(step)['template-filters'];
          if (!templates?.length) {
            return {};
          }
          return fetchPlatformObjectJsonSchema(templates[0]);
        });
        const schemas = await Promise.all(schemaRequests);
        const newStepContexts = newStepsWithUpdatedReferences.reduce<
          Record<string, UsableObjectConfig[]>
        >((hash, step, index) => {
          const combinedSchema = platformObjectJsonSchema
            ? { ...platformObjectJsonSchema, ...schemas[index] }
            : undefined;
          hash[step.token] = generateStepContext({
            step,
            criteriaVariableSchemas,
            platformObjectJsonSchema: combinedSchema,
            genericWorkflowActions,
            showErrorObjects,
          });
          return hash;
        }, {});
        dispatch(setStepContexts(newStepContexts));
      }
    },
    [
      criteriaVariableSchemas,
      dispatch,
      fetchPlatformObjectJsonSchema,
      genericWorkflowActions,
      inputStep,
      platformObjectJsonSchema,
      showErrorObjects,
      stepsConfig,
      stepsCopied,
      updateStepsConfig,
    ],
  );

  // Close config panel if viewing a step config and then version history button is clicked
  useEffect(() => {
    if (isShowingWorkflowVersions && activeNodeToken != null) {
      selectNode(null);
    }
  }, [isShowingWorkflowVersions, selectNode, activeNodeToken]);

  useEffect(() => {
    if (focusedValidationErrorIndex != null && validationErrors) {
      const focused = validationErrors[focusedValidationErrorIndex].token;
      selectNode(focused);
      if (clearFocusedValidationErrorIndex) {
        clearFocusedValidationErrorIndex();
      }
    }
  }, [focusedValidationErrorIndex, validationErrors, clearFocusedValidationErrorIndex, selectNode]);

  // handle delete unless inside of a textbox
  useKeydownDeleteListener((_) => {
    if (
      editable &&
      isDeleteableStepType(activeNode) &&
      document.activeElement &&
      PERMITTED_DOCUMENT_TAGS.includes(document.activeElement.tagName)
    ) {
      deleteStep(activeNode);
    }
  });

  const usableObjects = useUsableObjects(
    activeNodeToken && tokensToSteps[activeNodeToken] ? activeNodeToken : undefined,
  );

  return (
    <WorkflowActionMetaProvider
      context={actionMetaLocation}
      externalIntegrationVendors={externalIntegrationVendors}
    >
      <WorkflowEditorContainer
        aria-label='Editor for Workflow'
        isShowingHeaderStatus={isShowingHeaderStatus}
        role='application'
      >
        <FlowPlotterController>
          <WorkflowCanvas
            editable={editable}
            focusedNodeToken={focusedNodeToken}
            selectedNodeToken={activeNodeToken ?? null}
            erroredNodeTokens={
              isShowingWorkflowVersions ? [] : validationErrors.map(({ token }) => token)
            }
            steps={stepsConfig ?? []}
            inputStep={inputStep}
            addNodeAfter={handleAddNode}
            addNodeAfterBranchStart={handleAddFirstChildNode}
            moveStep={moveStep}
            selectNode={(selectedStep, { moduleToken }) =>
              selectNode(moduleToken ? moduleToken : selectedStep)
            }
          />
        </FlowPlotterController>
        {activeNode ? (
          <WorkflowControlPanel
            usableObjects={usableObjects}
            updateStep={updateStep}
            deleteStep={deleteStep}
            pasteCopiedSteps={pasteCopiedSteps}
            updateInputStep={updateInputStep}
            setConditionalRouteToEdit={setConditionalRouteToEdit}
            stepsConfig={stepsConfig}
            inputStep={inputStep}
            workflowSchema={workflowSchema}
            platformObjectJsonSchema={platformObjectJsonSchema}
            workflowName={parentName}
            workflowId={parentId}
            transactionTypes={transactionTypes}
            editable={editable}
            activeNode={activeNode}
            conditionalRouteToEdit={conditionalRouteToEdit}
            tokensToSteps={tokensToSteps}
            validationErrors={validationErrors}
            setFocusedNodeToken={wrappedSetFocusedNodeToken}
            closePanel={closePanel}
            workflowVersionOutput={outputs}
            updateWorkflowVersionOutput={updateVersionOutputs}
            isDirty={isDirty}
            isCurrentPublishedVersion={isCurrentPublishedVersion}
            workflowVersionExpiration={timeOutMinutes}
            canAddModule={canAddModule}
            updateWorkflowVersionExpiration={updateVersionExpiration}
            loadFilterFieldConfigs={loadFilterFieldConfigs}
          />
        ) : (
          isShowingWorkflowVersions && (
            <WorkflowVersionsSection
              latestDraftVersionId={latestDraftVersionId}
              currentVersionId={versionId}
              workflowDeployment={workflowDeployment}
              isDirty={isDirty}
              initialValues={{ description }}
              isLoadingVersionList={isLoadingVersionList}
              versionsList={versionsList}
              updateVersionDescription={updateVersionDescription}
              closeWorkflowVersionView={closeWorkflowVersionView}
              loadWorkflowVersion={loadWorkflowVersion}
              loadVersionList={loadVersionList}
              restoreDraftVersion={restoreDraftVersion}
              handleDiscardDraft={handleDiscardDraft}
            />
          )
        )}
      </WorkflowEditorContainer>
    </WorkflowActionMetaProvider>
  );
};

export default WorkflowEditor;
